import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, DropdownItemProps, DropdownProps, Grid } from 'semantic-ui-react';
import styled from 'styled-components';
import { FormikProps } from 'formik';

import { FieldLabel } from '../../../../../styles';
import { listProperties, PropertyWalletItemDTO } from '../../../../../services/properties';
import { maskZipCode } from '../../../../../services/masks';
import { CreateCapitalizationType } from '../Formik/types/CreateCapitalizationInitialValuesTypes';
import { mapWalletPropertyTypeToHireType } from '../../../../bond-insurance-budget-form/utils/mapWalletPropertyTypeToHireType';

type Props = Pick<FormikProps<CreateCapitalizationType>, 'setFieldValue' | 'values'> & {
  estateId?: string;
};

const getAddressLabel = (item: PropertyWalletItemDTO): string => {
  if (item?.address?.formatted) return item.address.formatted;

  return [
    item?.address?.street,
    item?.address?.number,
    item?.address?.complement,
    item?.address?.neighborhood,
    item?.address?.city,
    item?.address?.state
  ]
    .filter(Boolean)
    .join(', ');
};

export const WalletTag = styled.span`
  display: inline-block;
  font-size: 0.85rem;
  color: #2185d0;
  margin-bottom: 0.5rem;
`;

const CapitalizationPropertyWalletSearchDropdown = ({
  estateId,
  setFieldValue,
  values
}: Props): ReactElement | null => {
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<PropertyWalletItemDTO[]>([]);
  const requestIdRef = useRef(0);

  const selectedPublicId = values?.propertyPublicId;

  useEffect(() => {
    if (!estateId) {
      setItems([]);
      return;
    }

    const timeout = setTimeout(async () => {
      requestIdRef.current += 1;
      const currentRequestId = requestIdRef.current;
      setIsLoading(true);

      try {
        const response = await listProperties({
          estateId,
          search: search.trim() || undefined,
          page: 1,
          limit: 10
        });

        if (currentRequestId === requestIdRef.current) {
          setItems(response.items || []);
        }
      } catch (_error) {
        if (currentRequestId === requestIdRef.current) {
          setItems([]);
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [estateId, search]);

  const itemsByPublicId = useMemo(() => {
    const hash = new Map<string, PropertyWalletItemDTO>();
    items.forEach(item => {
      if (item?.publicId) hash.set(item.publicId, item);
    });
    return hash;
  }, [items]);

  const options: DropdownItemProps[] = items.map(item => ({
    key: item.publicId,
    value: item.publicId,
    text: item.identification || 'Sem código do imóvel',
    description: getAddressLabel(item)
  }));

  return (
    <Grid.Row>
      <Grid.Column width={16}>
        <FieldLabel>PESQUISAR NA CARTEIRA DE IMÓVEIS</FieldLabel>
        <Dropdown
          fluid
          selection
          clearable
          search
          loading={isLoading}
          options={options}
          value={selectedPublicId || ''}
          noResultsMessage="Nenhum imóvel encontrado."
          placeholder="Busque por CEP, bairro, logradouro ou código do imóvel"
          onSearchChange={(_event, data: DropdownProps) => {
            setSearch(String(data.searchQuery || ''));
          }}
          onChange={(_event, data: DropdownProps) => {
            const selectedId = String(data.value || '');

            if (!selectedId) {
              setFieldValue('propertyPublicId', undefined);
              setFieldValue('property.hasComplement', false);
              return;
            }

            const selected = itemsByPublicId.get(selectedId);
            if (!selected) return;

            const mappedPropertyType = mapWalletPropertyTypeToHireType(selected);

            setFieldValue('propertyPublicId', selected.publicId);
            setFieldValue('property.zipCode', maskZipCode(selected?.address?.zipcode || ''));
            setFieldValue('property.street', selected?.address?.street || '');
            setFieldValue('property.district', selected?.address?.neighborhood || '');
            setFieldValue('property.city', selected?.address?.city || '');
            setFieldValue('property.state', selected?.address?.state || '');
            setFieldValue('property.number', selected?.address?.number || '');
            setFieldValue('property.complement', selected?.address?.complement || '');
            setFieldValue(
              'property.hasComplement',
              !selected?.address?.complement?.trim()
            );
            setFieldValue('property.country', 'BR');
            if (mappedPropertyType) {
              setFieldValue('property.propertyType', mappedPropertyType);
            }
          }}
        />
      </Grid.Column>
    </Grid.Row>
  );
};

export default CapitalizationPropertyWalletSearchDropdown;
